import { Alert, DatePicker, Grid, Space, Tabs, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AttendancePatternsTab } from "../components/AttendancePatternsTab.tsx";
import { AttendanceSummaryTable } from "../components/AttendanceSummaryTable.tsx";
import { isRangeTooLong, MAX_RANGE_DAYS, toRange, type SummaryKind } from "../app/reports.ts";

type TabKey = SummaryKind | "patterns";

const DISPLAY_FORMAT = "DD.MM.YYYY";

/**
 * The director's attendance report (spec stories 48 and 49): counts and percentages by student, class and teacher for a date
 * range, and the unusual-pattern review list. Requires reports.read; only the open tab is fetched.
 */
export function AttendanceReportPage() {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const [dates, setDates] = useState<[Dayjs, Dayjs]>(() => [dayjs().startOf("month"), dayjs()]);
  const [tab, setTab] = useState<TabKey>("students");
  const range = toRange(dates[0], dates[1]);
  const tooLong = isRangeTooLong(range);

  const today = dayjs();
  const presets = [
    { label: t("reports.attendance.presets.thisWeek"), value: [today.startOf("week"), today] as [Dayjs, Dayjs] },
    { label: t("reports.attendance.presets.thisMonth"), value: [today.startOf("month"), today] as [Dayjs, Dayjs] },
    { label: t("reports.attendance.presets.lastMonth"), value: [today.subtract(1, "month").startOf("month"), today.subtract(1, "month").endOf("month")] as [Dayjs, Dayjs] },
    { label: t("reports.attendance.presets.last30"), value: [today.subtract(29, "day"), today] as [Dayjs, Dayjs] },
  ];

  const summary = (kind: SummaryKind) => <AttendanceSummaryTable kind={kind} range={range} enabled={tab === kind && !tooLong} />;

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 16 }}>
        <div style={{ flex: "1 1 360px", minWidth: 0 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {t("reports.attendance.title")}
          </Typography.Title>
          <Typography.Text type="secondary">{t("reports.attendance.subtitle")}</Typography.Text>
        </div>
        <Space orientation="vertical" size={4} style={{ width: screens.md ? "auto" : "100%" }}>
          <Typography.Text strong>{t("reports.attendance.range")}</Typography.Text>
          <DatePicker.RangePicker
            value={dates}
            format={DISPLAY_FORMAT}
            allowClear={false}
            presets={presets}
            aria-label={t("reports.attendance.range")}
            style={{ width: screens.md ? 280 : "100%" }}
            disabledDate={(current, info) => (info.from ? Math.abs(current.diff(info.from, "day")) >= MAX_RANGE_DAYS : false)}
            onChange={(value) => {
              if (value?.[0] && value[1]) setDates([value[0], value[1]]);
            }}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t("reports.attendance.rangeHint")}
          </Typography.Text>
        </Space>
      </div>
      {tooLong && <Alert type="warning" showIcon title={t("reports.attendance.rangeTooLong")} />}
      <Tabs
        activeKey={tab}
        onChange={(key) => setTab(key as TabKey)}
        items={[
          { key: "students", label: t("reports.attendance.tabs.students"), children: summary("students") },
          { key: "classes", label: t("reports.attendance.tabs.classes"), children: summary("classes") },
          { key: "teachers", label: t("reports.attendance.tabs.teachers"), children: summary("teachers") },
          { key: "patterns", label: t("reports.attendance.tabs.patterns"), children: <AttendancePatternsTab range={range} enabled={tab === "patterns" && !tooLong} /> },
        ]}
      />
    </Space>
  );
}
